import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './StartPlanModal.css';

const StartPlanModal = ({ plan, reader, onStart, onClose }) => {
  const [targetEndDate, setTargetEndDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [useSmartDefaults, setUseSmartDefaults] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const loadingRef = useRef(false);
  loadingRef.current = loading;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !loadingRef.current) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!useSmartDefaults) {
      if (!targetEndDate) {
        setError('Please select a target end date');
        return;
      }

      const selectedEndDate = new Date(targetEndDate);
      const selectedStartDate = startDate ? new Date(startDate) : new Date();
      
      if (selectedEndDate <= selectedStartDate) {
        setError('Target end date must be after start date');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');
      
      const options = useSmartDefaults ? {} : {
        targetEndDate: targetEndDate ? new Date(targetEndDate).toISOString() : null,
        startDate: startDate ? new Date(startDate).toISOString() : null
      };
      
      await onStart(plan.id, reader.id, options);
    } catch (err) {
      console.error('Error starting plan:', err);
      setError(err.response?.data?.error || 'Failed to start plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const getSmartDefaultDates = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // October reading focus dates
    const octoberStart = new Date(currentYear, 8, 28); // September 28th (0-indexed month)
    const octoberEnd = new Date(currentYear, 10, 2);   // November 2nd (0-indexed month)
    
    // If we're before September 28th, default to October focus dates
    if (now < octoberStart) {
      return {
        startDate: octoberStart.toISOString().split('T')[0],
        endDate: octoberEnd.toISOString().split('T')[0],
        description: 'October reading focus (Sept 28 - Nov 2)'
      };
    }
    
    // If we're after September 28th but before November 2nd, use current date to November 2nd
    if (now >= octoberStart && now <= octoberEnd) {
      return {
        startDate: now.toISOString().split('T')[0],
        endDate: octoberEnd.toISOString().split('T')[0],
        description: 'Current to November 2nd'
      };
    }
    
    // If we're after November 2nd, use current date + estimated days
    if (plan.estimatedDays) {
      const suggestedEnd = new Date();
      suggestedEnd.setDate(suggestedEnd.getDate() + plan.estimatedDays);
      return {
        startDate: now.toISOString().split('T')[0],
        endDate: suggestedEnd.toISOString().split('T')[0],
        description: `Current + ${getEstimatedDaysText(plan.estimatedDays)}`
      };
    }
    
    return {
      startDate: now.toISOString().split('T')[0],
      endDate: '',
      description: 'Current date (flexible end)'
    };
  };

  const getSuggestedEndDate = () => {
    if (!plan.estimatedDays) return '';
    
    const suggestedDate = new Date();
    suggestedDate.setDate(suggestedDate.getDate() + plan.estimatedDays);
    return suggestedDate.toISOString().split('T')[0];
  };

  const getEstimatedDaysText = (days) => {
    if (!days) return 'Flexible duration';
    if (days === 1) return '1 day';
    if (days < 7) return `${days} days`;
    if (days < 30) return `${Math.round(days / 7)} weeks`;
    if (days < 365) return `${Math.round(days / 30)} months`;
    return `${Math.round(days / 365)} years`;
  };

  return createPortal(
    <div className="start-plan-overlay" onClick={onClose}>
      <div
        className="start-plan-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="start-plan-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="start-plan-header">
          <h2 id="start-plan-title">Start plan</h2>
          <button className="start-plan-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="start-plan-body">
          <div className="start-plan-summary">
            <h3>{plan.name}</h3>
            <p className="start-plan-description">
              {plan.description || 'A structured plan to guide your Bible study journey.'}
            </p>
            
            <div className="start-plan-details">
              <div className="start-plan-detail">
                <span className="start-plan-detail-label">Reader:</span>
                <span className="start-plan-detail-value">{reader.name}</span>
              </div>
              <div className="start-plan-detail">
                <span className="start-plan-detail-label">Chapters:</span>
                <span className="start-plan-detail-value">{plan.chapterCount || 'N/A'}</span>
              </div>
              <div className="start-plan-detail">
                <span className="start-plan-detail-label">Estimated duration</span>
                <span className="start-plan-detail-value">{getEstimatedDaysText(plan.estimatedDays)}</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="start-plan-form">
            <div className="start-plan-field">
              <div className="start-plan-defaults">
                <label className="start-plan-toggle">
                  <input
                    type="checkbox"
                    checked={useSmartDefaults}
                    onChange={(e) => setUseSmartDefaults(e.target.checked)}
                  />
                  <span className="start-plan-toggle-text">Use smart defaults</span>
                </label>
                {useSmartDefaults && (
                  <div className="start-plan-defaults-info">
                    <span className="start-plan-defaults-badge">🎯</span>
                    <span className="start-plan-defaults-description">
                      {getSmartDefaultDates().description}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {!useSmartDefaults && (
              <>
                <div className="start-plan-field">
                  <label htmlFor="startDate">
                    Start date
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={targetEndDate || undefined}
                    className="start-plan-date"
                  />
                  <p className="start-plan-help">
                    When would you like to start this plan? (Optional - defaults to today)
                  </p>
                </div>

                <div className="start-plan-field">
                  <label htmlFor="targetEndDate">
                    Target completion date *
                  </label>
                  <input
                    type="date"
                    id="targetEndDate"
                    value={targetEndDate}
                    onChange={(e) => setTargetEndDate(e.target.value)}
                    min={startDate || getMinDate()}
                    required
                    className="start-plan-date"
                  />
                  {plan.estimatedDays && (
                    <button
                      type="button"
                      className="start-plan-suggested"
                      onClick={() => setTargetEndDate(getSuggestedEndDate())}
                    >
                      Use suggested date ({getEstimatedDaysText(plan.estimatedDays)})
                    </button>
                  )}
                  <p className="start-plan-help">
                    Choose when you'd like to complete this plan. The app will automatically adjust your daily reading requirements to help you stay on track.
                  </p>
                </div>
              </>
            )}

            {error && (
              <div className="start-plan-error">
                {error}
              </div>
            )}

            <div className="start-plan-actions">
              <button
                type="button"
                className="start-plan-cancel"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="start-plan-submit"
                disabled={loading || (!useSmartDefaults && !targetEndDate)}
              >
                {loading ? 'Starting plan...' : (useSmartDefaults ? 'Start plan with smart defaults' : 'Start plan')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StartPlanModal;
